import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Avatar,
  Stack,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "../../api/axiosInstance";
import { getHeader } from "../../types";

/* ---------------------------------------------------------------
   DESIGN TOKENS — mirrors the palette from the target mock.
---------------------------------------------------------------- */
const C = {
  primaryGreen: "#38CFAB",
  primaryBlue: "#4F6C8D",
  lightGreen: "#92F7DF",
  lightBlue: "#E0FBFF",
  warning: "#CA8A04",
  lightYellow: "#FEFCE8",
  sectionBg: "#F3F4F6",
  border: "#E3E3E3",
  buttonBg: "#F0F0F0",
  white: "#FFFFFF",
  textMain: "#504949",
  textMuted: "#999999",
  error: "#FF6565",
  errorBg: "#FFF1F1",
};

const TONE_STYLE = {
  good: { fg: "#1E9C7C", bg: C.lightGreen, Icon: CheckCircleIcon },
  warn: { fg: C.warning, bg: C.lightYellow, Icon: WarningAmberIcon },
  bad: { fg: C.error, bg: C.errorBg, Icon: CancelIcon },
  info: { fg: C.primaryBlue, bg: C.lightBlue, Icon: InfoIcon },
  empty: { fg: C.textMuted, bg: C.sectionBg, Icon: WarningAmberIcon },
};

// Maps the AI's severity label (from `insights[].severity`) to a tone.
// Unknown/missing severities fall back to "info" rather than being dropped.
const SEVERITY_TONE = { high: "bad", medium: "warn", low: "info" };

/* ---------------------------------------------------------------
   FUNNEL METRIC CONFIG — keys as returned in conversionFunnel.
---------------------------------------------------------------- */
const FUNNEL_LABELS = {
  curatedToShortlisted: "Curated → Shortlisted",
  shortlistedToInterviewed: "Shortlisted → Interviewed",
  interviewedToOffered: "Interviewed → Offered",
  offeredToHired: "Offered → Hired",
  overallConversion: "Overall Conversion",
};
// Shown as the primary chip row, in this order. Add/remove keys here to
// surface other conversionFunnel fields (e.g. curatedToScreenReject).
const PRIMARY_FUNNEL_KEYS = [
  "curatedToShortlisted",
  "shortlistedToInterviewed",
  "interviewedToOffered",
  "offeredToHired",
  "overallConversion",
];
const LOWER_IS_BETTER = new Set(["curatedToScreenReject", "interviewR1ToReject"]);

/* ---------------------------------------------------------------
   HELPERS
---------------------------------------------------------------- */
function totalOf(value) {
  if (value && typeof value === "object" && "total" in value) return value.total;
  return Number(value) || 0;
}

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

const fmtPct = (v) =>
  v === null || v === undefined || Number.isNaN(v) ? "—" : `${Math.round(v)}%`;

// Splits a string on *bolded* segments (e.g. "the *Screening Feedback* stage")
// and renders the starred parts as bold, highlighted <strong> spans.
function renderHighlightedText(text) {
  if (typeof text !== "string" || !text) return text;
  const parts = text.split(/\*([^*]+)\*/g);
  // Even indices are plain text, odd indices are the captured bold groups.
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <Box
        key={i}
        component="strong"
        sx={{ fontWeight: 700, color: C.textMain }}
      >
        {part}
      </Box>
    ) : (
      part
    )
  );
}

function humanizeKey(key) {
  return String(key)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

// Compares the recruiter's value on a conversionFunnel metric against the
// team's value on the same metric to decide the chip's tone.
function metricTone(key, recruiterVal, teamVal) {
  if (recruiterVal === null || recruiterVal === undefined) return "empty";
  if (teamVal === null || teamVal === undefined) return "info";

  const lowerIsBetter = LOWER_IS_BETTER.has(key);
  const diff = lowerIsBetter ? teamVal - recruiterVal : recruiterVal - teamVal;

  if (teamVal === 0) {
    if (recruiterVal === 0) return "info";
    return diff > 0 ? "good" : "bad";
  }
  const relDiff = diff / Math.abs(teamVal);
  if (relDiff >= 0.15) return "good";
  if (relDiff <= -0.15) return "bad";
  return "warn";
}

function buildFunnelMetrics(row, teamSummary) {
  const recruiterFunnel = row?.conversionFunnel || {};
  const teamFunnel = teamSummary?.conversionFunnel || {};

  return PRIMARY_FUNNEL_KEYS.filter((key) => key in recruiterFunnel).map((key) => {
    const recruiterVal = typeof recruiterFunnel[key] === "number" ? recruiterFunnel[key] : null;
    const teamVal = typeof teamFunnel[key] === "number" ? teamFunnel[key] : null;
    return {
      key,
      title: FUNNEL_LABELS[key] || humanizeKey(key),
      value: recruiterVal,
      teamValue: teamVal,
      tone: metricTone(key, recruiterVal, teamVal),
    };
  });
}

function worstMetric(metrics) {
  const rank = { bad: 3, warn: 2, info: 1, good: 0, empty: 0 };
  const scored = metrics.filter((m) => m.tone === "bad" || m.tone === "warn");
  if (scored.length === 0) return null;
  return scored.reduce((worst, m) => (rank[m.tone] > rank[worst.tone] ? m : worst), scored[0]);
}

/* ---------------------------------------------------------------
   SUBCOMPONENTS
---------------------------------------------------------------- */
function FunnelStrip({ row }) {
  const stages = [
    { label: "Curated", value: totalOf(row.curated) },
    { label: "Shortlisted", value: totalOf(row.shortlisted) },
    { label: "Interviewed", value: totalOf(row.interviewed) },
    { label: "Offered", value: totalOf(row.offered) },
    { label: "Hired", value: totalOf(row.hired) },
  ];
  const max = Math.max(...stages.map((s) => s.value), 1);

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-end">
      {stages.map((s, i) => (
        <Box key={s.label} sx={{ flex: 1, textAlign: "center" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: C.textMain, mb: 0.5 }}>
            {s.value}
          </Typography>
          <Box sx={{ height: 40, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <Box
              sx={{
                width: "100%",
                maxWidth: 34,
                height: `${Math.max((s.value / max) * 100, 6)}%`,
                borderRadius: "3px 3px 0 0",
                bgcolor: i === 0 ? C.primaryBlue : C.primaryGreen,
              }}
            />
          </Box>
          <Typography sx={{ fontSize: 10.5, color: C.textMuted, mt: 0.5 }}>{s.label}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

function StatChip({ metric }) {
  const t = TONE_STYLE[metric.tone];
  const hasTeam = metric.teamValue !== null && metric.teamValue !== undefined;
  const beatsTeam = hasTeam && metric.value !== null && metric.value > metric.teamValue;
  const belowTeam = hasTeam && metric.value !== null && metric.value < metric.teamValue;

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ border: `1px solid ${C.border}`, borderRadius: 5, px: 2, py: 1, bgcolor: C.white }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: t.fg, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, color: C.textMain, whiteSpace: "nowrap" }}>{metric.title}</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: t.fg }}>{fmtPct(metric.value)}</Typography>
      {hasTeam && (
        <Stack direction="row" spacing={0.3} alignItems="center" sx={{ color: C.textMuted }}>
          {beatsTeam && <ArrowUpwardIcon sx={{ fontSize: 12, color: "#1E9C7C" }} />}
          {belowTeam && <ArrowDownwardIcon sx={{ fontSize: 12, color: C.error }} />}
          <Typography sx={{ fontSize: 11, whiteSpace: "nowrap" }}>
            team {fmtPct(metric.teamValue)}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}

function ReasonBar({ reason, count, max }) {
  const width = max > 0 ? Math.max((count / max) * 100, 4) : 0;
  return (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Typography sx={{ fontSize: 12, color: C.textMain, width: 170, flexShrink: 0 }}>{reason}</Typography>
      <Box sx={{ flex: 1, height: 16, bgcolor: C.sectionBg, borderRadius: 1, overflow: "hidden" }}>
        <Box sx={{ width: `${width}%`, height: "100%", bgcolor: C.primaryBlue, borderRadius: 1 }} />
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.textMain, width: 22, textAlign: "right" }}>
        {count}
      </Typography>
    </Stack>
  );
}

// insights[]: { title, detail, recommendation, severity }
function InsightCard({ insight }) {
  const tone = SEVERITY_TONE[insight?.severity] || "info";
  const t = TONE_STYLE[tone];
  return (
    <Box
      sx={{
        bgcolor: C.white,
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${t.fg}`,
        borderRadius: 1.5,
        p: 1.6,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 0.6 }}>
        <t.Icon sx={{ fontSize: 15, color: t.fg, mt: 0.2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textMain }}>
          {insight?.title || "Insight"}
        </Typography>
      </Stack>
      {insight?.detail && (
        <Typography sx={{ fontSize: 12.5, lineHeight: "19px", color: C.textMain, mb: insight?.recommendation ? 0.8 : 0 }}>
          {renderHighlightedText(insight.detail)}
        </Typography>
      )}
      {insight?.recommendation && (
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <LightbulbIcon sx={{ fontSize: 14, color: C.primaryBlue, mt: 0.2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12.5, lineHeight: "19px", color: C.textMain, fontWeight: 500 }}>
            {renderHighlightedText(insight.recommendation)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------
   MODAL
---------------------------------------------------------------- */
export default function InsightsModal({ open, onClose, row, startDate, endDate }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !row?.email || !startDate || !endDate) return;

    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);

    axios
      .post(
        "/api/dashboard/recruiter-dashboard/insights",
        { startDate, endDate, email: row.email },
        { headers: getHeader() }
      )
      .then((res) => {
        if (cancelled) return;
        if (res.data?.success === false) {
          setError(res.data?.message || "Failed to load insights.");
          return;
        }
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to fetch recruiter insights", err);
        setError(err.response?.data?.message || "Failed to load insights. Please retry.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, row?.email, startDate, endDate]);

  const metrics = data ? buildFunnelMetrics(data, data.teamSummary) : [];
  const priority = worstMetric(metrics);
  const priorityStyle = priority ? TONE_STYLE[priority.tone] : null;

  const dropReasons = data?.whyCandidatesAreDropping
    ? Object.entries(data.whyCandidatesAreDropping)
        .map(([reason, count]) => ({ reason: humanizeKey(reason), count: Number(count) || 0 }))
        .sort((a, b) => b.count - a.count)
    : [];
  const maxDropCount = Math.max(...dropReasons.map((r) => r.count), 1);
  const hasDropData = dropReasons.some((r) => r.count > 0);

  const insights = Array.isArray(data?.insights) ? data.insights : [];

  const curatedCount = data ? totalOf(data.curated) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2.2,
          borderBottom: `1px solid ${C.border}`,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar sx={{ width: 38, height: 38, fontSize: 13, fontWeight: 600, bgcolor: C.primaryBlue }}>
            {initials(row?.name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 16, fontWeight: 600, color: C.textMain }}>{row?.name}</Typography>
            <Typography sx={{ fontSize: 12, color: C.textMuted }}>
              {[row?.role, data?.pod_group].filter(Boolean).join(" · ")}
              {row?.role || data?.pod_group ? " · " : ""}Performance insights
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: C.buttonBg }}>
          <CloseIcon sx={{ fontSize: 16, color: C.textMain }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, py: 3 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ textAlign: "center", py: 6, color: C.error, fontSize: 14 }}>{error}</Box>
        )}

        {!loading && !error && data && (
          <>
            <Box sx={{ mb: 2.5 }}>
              <FunnelStrip row={data} />
            </Box>

            {priority && (
              <Stack
                direction="row"
                spacing={1.2}
                alignItems="flex-start"
                sx={{
                  border: `1px solid ${priorityStyle.fg}33`,
                  bgcolor: priorityStyle.bg,
                  borderRadius: 2,
                  p: 1.6,
                  mb: 2.5,
                }}
              >
                <priorityStyle.Icon sx={{ fontSize: 16, color: priorityStyle.fg, mt: 0.2, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 12.5, color: C.textMain, lineHeight: "18px" }}>
                  <Box component="span" sx={{ fontWeight: 700, color: priorityStyle.fg }}>
                    Weakest signal: {priority.title}
                  </Box>{" "}
                  at {fmtPct(priority.value)}
                  {priority.teamValue !== null && priority.teamValue !== undefined
                    ? ` (team: ${fmtPct(priority.teamValue)})`
                    : ""}
                  .
                </Typography>
              </Stack>
            )}

            {metrics.length > 0 && (
              <Stack direction="row" flexWrap="wrap" gap={1.2} sx={{ mb: 3 }}>
                {metrics.map((m) => (
                  <StatChip key={m.key} metric={m} />
                ))}
              </Stack>
            )}

            {insights.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.3 }}>
                  <LightbulbIcon sx={{ fontSize: 15, color: C.primaryBlue }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textMain }}>
                    Recommendations
                  </Typography>
                </Stack>
                <Stack spacing={1.2}>
                  {insights.map((insight, i) => (
                    <InsightCard key={i} insight={insight} />
                  ))}
                </Stack>
              </Box>
            )}

            <Box sx={{ border: `1px solid ${C.border}`, borderRadius: 2, p: 2 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: C.textMain, mb: 1 }}>
                Why candidates are dropping
              </Typography>
              {hasDropData ? (
                <Stack spacing={1.2} sx={{ mt: 1.5 }}>
                  {dropReasons.map((r) => (
                    <ReasonBar key={r.reason} reason={r.reason} count={r.count} max={maxDropCount} />
                  ))}
                </Stack>
              ) : (
                <Box sx={{ py: 3, textAlign: "center", fontSize: 12, color: C.textMuted }}>
                  No rejections logged this period.
                </Box>
              )}
            </Box>

            {curatedCount > 0 && curatedCount < 10 && (
              <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 1.8 }}>
                Note: sample size is small ({curatedCount} curated) — treat these numbers as directional.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}